import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { Order } from '../Types/Order'
import { OrderItem } from '../Types/OrderItem'
import { FilterStatus } from '../Types/Enums'
import { customerOrderStatus } from '~shared/util/constants'

dayjs.extend(isSameOrAfter)

export const OrderUtil = {
  /**
   * Determines the next status based on the current status and order details.
   * @param status - The current status of the order.
   * @param order - The order object.
   * @returns {string} The next status of the order.
   */
  nextStatus: (order: Order, currentFilter: FilterStatus): string => {
    const status = order.status
    let isUpcomingTab = currentFilter === FilterStatus.Upcoming

    //this logic should all be moved server side
    if (status === customerOrderStatus.New && isUpcomingTab) {
      return customerOrderStatus.DepartmentConfirmed
    }

    switch (status) {
      case customerOrderStatus.New:
        return customerOrderStatus.Confirmed
      case customerOrderStatus.DepartmentConfirmed:
        return customerOrderStatus.Confirmed
      case customerOrderStatus.Confirmed:
        return customerOrderStatus.Complete
      default:
        return customerOrderStatus.Complete
    }
  },

  /**
   * Retrieves the appropriate button label based on the order status and current filter.
   * @param status - The current status of the order.
   * @param config - An object containing label strings.
   * @param currentFilter - The current filter applied to the orders list.
   * @param hasDeferredPayment - Indicates whether the order has a deferred payment.
   * @param weightRequiresConfirmation - Indicates whether the order requires weight confirmation.
   * @returns {string} The button label to display.
   */
  buttonLabel: (
    status: string,
    currentFilter?: string,
    hasDeferredPayment?: boolean,
    weightRequiresConfirmation?: boolean,
    invoicingDelay?: boolean
  ): string => {
    switch (status) {
      case customerOrderStatus.DepartmentConfirmed:
        return currentFilter === 'Active' ? 'Start Preparing' : 'Confirm Order'
      case customerOrderStatus.New:
        if (currentFilter === 'Active') {
          if (hasDeferredPayment && !weightRequiresConfirmation) {
            return 'Pay & Confirm'
          }
          return 'Confirm Order'
        }
        return 'Confirm Order'
      case customerOrderStatus.Confirmed:
        return hasDeferredPayment && !invoicingDelay ? 'Pay & Complete' : 'Complete Order'
      case customerOrderStatus.Complete:
        return 'Completed'
      default:
        return 'Confirm Order'
    }
  },

  /**
   * Parses a leadTime string in the format "HH:mm:ss.ffffff" and returns the total minutes.
   * @param leadTime - The leadTime string to parse.
   * @returns {number} Total minutes represented by the leadTime.
   */
  parseLeadTimeToMinutes: (leadTime: string): number => {
    const regex = /^(\d{2}):(\d{2}):([\d.]+)$/
    const matches = regex.exec(leadTime)

    if (matches && matches.length >= 3) {
      const hours = parseInt(matches[1], 10)
      const minutes = parseInt(matches[2], 10)
      const seconds = parseFloat(matches[3])

      const totalMinutes = hours * 60 + minutes + seconds / 60
      return totalMinutes
    }

    return NaN // Return NaN if parsing fails
  },

  /**
   * Calculates the maximum lead time among all items in an order.
   * If no item has a lead time, returns 0.
   * @param items - Array of OrderItem.
   * @returns {number} Maximum lead time in minutes.
   */
  getMaxLeadTime: (items: OrderItem[]): number => {
    const maxLeadTime = items.reduce((max: number, item) => {
      if (item.leadTime) {
        const itemLeadTime = OrderUtil.parseLeadTimeToMinutes(item.leadTime)

        if (!isNaN(itemLeadTime)) {
          return Math.max(max, itemLeadTime)
        }
      }
      return max
    }, 0)

    return maxLeadTime
  },

  /**
   * Counts the number of completed orders in the provided array.
   * @param orders - Array of Order.
   * @returns {number} Count of completed orders.
   */
  countCompletedOrders: (orders: Order[]): number => {
    return orders?.filter((order: Order) => order.status === customerOrderStatus.Complete)?.length
  },

  isPaid: (order: Order): boolean => {
    return order.balance === 0 && (order.paymentTotal ?? 0) > 0
  },
}
